import { Component, computed, effect, input, InputSignal, output, OutputEmitterRef, signal, Signal } from '@angular/core';
import { Regimen, User } from "@cureskin/api-client/src/server-api";
import { CsIconModule } from '@components/icon';
import { CommonModule } from '@angular/common';
import { ApiClientConstant } from '@cureskin/api-client';
import { planName, selectedPlanAndVariantIdMap } from '@services/regimen-service';
import { getPlanInfoConfig, PlanInfoItem, planInfoItemId, planInfoImageUrls } from '../choose-plan-info/choose-plan-info.constants';

@Component({
  selector: 'regimen-value-proposition-v2',
  imports: [CsIconModule, CommonModule],
  templateUrl: './regimen-value-proposition-v2.component.html',
})
export class RegimenValuePropositionV2Component {
  regimenUpdateTrigger: InputSignal<Regimen> = input();
  regimenChangeSignal: InputSignal<Regimen> = input();

  //readonly input signal
  regimenData = signal<Regimen | null>(null);

  user: InputSignal<User> = input();
  backgroundColor: InputSignal<string> = input();
  allocatedDoctorImage: InputSignal<string> = input();
  videoCallExperiment: InputSignal<boolean> = input(false);
  patientName: Signal<string> = computed(() => this.user()?.get('PatientName')?.split(' ')[0] || '');
  regimenProductsLength: Signal<number> = computed(() => this.regimenData()?.products?.length || 0);
  readMoreClick: OutputEmitterRef<void> = output();
  readonly ApiClientConstant = ApiClientConstant;
  planInfoItemId = planInfoItemId;

  // Cache the config to avoid creating new objects on every access
  private _planInfoConfigCache: ReturnType<typeof getPlanInfoConfig> | null = null;
  private _lastCacheKey: string | null = null;

  // Computed signal to get current plan info config based on variantId
  currentPlanInfo = computed(() => {
    const regimenData = this.regimenData();
    const variantId = (regimenData as any)?.variantId;
    const selectedVariantId = (regimenData as any)?.selectedVariantId || variantId;
    const regimenClass = (regimenData as any)?.class;
    const videoCall = this.videoCallExperiment();
    const currentPlan = Object.values(planName).find(
      (plan) => selectedPlanAndVariantIdMap[plan] === selectedVariantId,
    );
    const cacheKey = `${regimenClass}-${selectedVariantId}-${videoCall}`;
    if (!this._planInfoConfigCache || this._lastCacheKey !== cacheKey) {
      this._planInfoConfigCache = getPlanInfoConfig([], videoCall, regimenClass);
      this._lastCacheKey = cacheKey;
    }
    return this._planInfoConfigCache[currentPlan || planName.advance];
  });

  constructor() {
    effect(() => {
      const updatedRegimen = this.regimenUpdateTrigger();
      if (updatedRegimen) {
        this.regimenData.set(updatedRegimen);
      }
    });
  }

  readMoreAboutServices(): void {
    this.readMoreClick.emit();
  }

  getItemImage(item: PlanInfoItem): string {
    if (item.id === planInfoItemId.PRODUCTS && this.regimenData()?.regimenCoverImage) {
      return this.regimenData()?.regimenCoverImage;
    }
    if ((item.id === planInfoItemId.DEDICATED_DOCTOR || item.id === planInfoItemId.ONE_ON_ONE_DOCTOR) && this.allocatedDoctorImage()) {
      return this.allocatedDoctorImage();
    }
    switch (item.id) {
      case planInfoItemId.MONTHLY_CHECKUPS:
        return planInfoImageUrls.CHECKUP_REPORT_SQUARE;
      case planInfoItemId.NUTRITIONIST_SESSION:
      case planInfoItemId.NUTRITIONIST_SESSIONS:
        return planInfoImageUrls.CALL_ICONS_SQUARE;
      case planInfoItemId.DIET_PLAN:
        return planInfoImageUrls.DIET_PLAN_SQUARE;
      default:
        return item.imageUrl;
    }
  }
}
